import { z } from "zod";
import { Scene } from "./scene.js";
import { TOUR_STOPS_MAX, TourStop } from "./tour.js";

export const ShotType = z.enum(["hook", "product", "detail", "people", "text_card", "end_card"]);
export type ShotType = z.infer<typeof ShotType>;
/** How a picture moves: not at all, a slow camera, a clip made by the video model, or a tour of the photo (`tour.ts`). */
export const Motion = z.enum(["none", "kenburns", "image_to_video", "tour"]);
export type Motion = z.infer<typeof Motion>;

export const Shot = z.object({
  position: z.number().int().min(0),
  type: ShotType,
  description: z.string().min(1).max(600),
  durationMs: z.number().int().min(500).max(20_000),
  dialogue: z.string().max(400).optional(),
  isNarration: z.boolean().default(true),
  onScreenText: z.string().max(80).optional(),
  imagePrompt: z.string().max(1_000),
  /** Set when the shot composes from one of the business's real photos (compose-first). Null means generate. */
  sourceMediaId: z.uuid().nullable(),
  motion: Motion.default("kenburns"),
  /** A drawn scene in place of a picture (ADR 0050): no photo, no generated image, no motion to pay for. */
  scene: Scene.nullable().default(null),
  /** The stops of a photo's tour, when its motion is "tour": regions of its photo, in the order the camera visits them. */
  tour: z.array(TourStop).min(1).max(TOUR_STOPS_MAX).nullable().default(null),
});
export type Shot = z.infer<typeof Shot>;

/**
 * How long a post is on screen, as the one shot it is (03: "Post is one image with text"). A post is rendered like a
 * video, a picture with its words laid out over it, so it has a length; this is it, whatever a model writes. The script
 * of a post is told it, and held to it (`asPost` in the worker): a post written as four cards over seventeen seconds was
 * made as exactly that (the first pilot round, 2026-10-05). The test double has always written a post this long.
 */
export const POST_MS = 5_000;

/** The script's fields before the rules that tie them together; `Script` is this with those rules. */
export const ScriptBase = z.object({
  premise: z.string().min(1).max(300),
  tone: z.string().min(1).max(100),
  audience: z.string().min(1).max(200),
  coreMessage: z.string().min(1).max(200),
  durationMs: z.number().int().min(3_000).max(60_000),
  shots: z.array(Shot).min(1).max(12),
});

export const Script = ScriptBase
  .superRefine((s, ctx) => {
    const sum = s.shots.reduce((a, sh) => a + sh.durationMs, 0);
    if (Math.abs(sum - s.durationMs) > 1_500) {
      ctx.addIssue({ code: "custom", message: `shots sum to ${sum}ms, script says ${s.durationMs}ms` });
    }
    s.shots.forEach((sh, i) => {
      if (sh.position !== i) ctx.addIssue({ code: "custom", path: ["shots", i, "position"], message: "positions must be 0..n-1 in order" });
    });
    const used = s.shots.map((sh) => sh.sourceMediaId).filter((x): x is string => !!x);
    if (new Set(used).size !== used.length) {
      ctx.addIssue({ code: "custom", path: ["shots"], message: "a source photo may be used by one shot only" });
    }
  });
export type Script = z.infer<typeof Script>;
